/**
 * Shared PostgREST `select()` strings for the AR/AP invoice DETAIL read.
 *
 * One string per invoice type, interpolated by every consumer of the detail
 * read — the screen's lazy per-invoice query (`useARInvoiceById`, and the
 * `GET /api/v1/ap-invoices/orgs/:orgId/ap-invoices/:invoiceId` handler that
 * backs `useAPInvoiceById`) AND the invoice document assembler in this
 * directory. Same reasoning as `lib/accountingSelects.ts`: a copy-pasted
 * embed drifts silently — a column dropped from one copy produces no type
 * error, just a field that is quietly `undefined` on a customer-facing PDF.
 * (PostgREST ignores whitespace, so the layout here is equivalent to the
 * previous inline literals.)
 *
 * These are the FAT detail selects, byte-equivalent to what those two reads
 * already sent — deliberately, because the whole point is that no consumer
 * gets a different projection. The document over-fetches a little (it never
 * prints ledger entries or import links) but it is one invoice per render,
 * and the posting journal's `tax_rate` embed is in fact what names the rate
 * groups in the document's tax breakdown.
 *
 * One asymmetry between AR and AP is load-bearing and preserved: the AP
 * detail select carries NO project embed (neither top-level nor nested) while
 * AR carries both — its consumers were built against that (see the route's
 * own note; the AP fat LIST select has them). The assembler resolves an AP
 * bill's project with a separate one-row read instead of widening this string.
 * (The line-item selects used to differ too — AR had no `tax_rate_id` — until
 * the channel-sale edit form needed it to prefill a line's tax-rate picker.)
 */
import { ACCOUNT_TYPE_EMBED, IMPORT_MATCH_EMBED } from "../../lib/accountingSelects.js";
/**
 * AR invoice detail — the `accounts_receivable` row plus person/project, the
 * posting journal (with entries + their tax rates) and the RAW
 * `payment_transactions` array.
 *
 * `select('*')` on the header on purpose: a pinned column list is where
 * byte-compatibility breaks (the AR edit form alone reads a dozen header
 * fields). `void_of_transaction_id` is load-bearing — it keys
 * `filterPaymentTransactions`; dropping it silently turns void journals and
 * force-edit-superseded postings back into "payments".
 *
 * The account embeds carry `currency` (AP's do not): the AR surfaces preserve
 * the per-invoice account currency — the expansion panel renders entry amounts
 * in the AR account's own currency rather than the org base. NOTHING TYPECHECKS
 * THIS: the row types declare `currency?: string | null`, so dropping it from
 * this string is a silently-`undefined` amount label, not a compile error.
 * `useAccountsReceivable.test.ts` asserts the literal substring instead.
 *
 * `void_transaction` is the reversal journal `cancel_ar_invoice` posts
 * (`accounts_receivable.void_transaction_id`); only its `transaction_date` is
 * read — the document's "Cancelled on <date>" (`cancelledOn`).
 */
export const AR_INVOICE_DOCUMENT_SELECT = `
  *,
  person:people(first_name, last_name, email),
  project:projects(id, name, code),
  ledger_transaction:ledger_transactions!ledger_transaction_id(
    transaction_number,
    fund_id,
    project_id,
    reference,
    ${IMPORT_MATCH_EMBED},
    entries:ledger_entries(
      *,
      chart_of_accounts:chart_of_accounts(id, account_name, currency, ${ACCOUNT_TYPE_EMBED}),
      tax_rate:tax_rates(name, rate, direction)
    )
  ),
  payment_transactions:ledger_transactions!ar_invoice_id(
    id,
    transaction_number,
    transaction_date,
    description,
    total_amount,
    reference,
    fund_id,
    void_of_transaction_id,
    ${IMPORT_MATCH_EMBED},
    payment_import_matches:accounting_import_matches!payment_transaction_id(id),
    entries:ledger_entries(
      *,
      chart_of_accounts:chart_of_accounts(id, account_name, currency, ${ACCOUNT_TYPE_EMBED}),
      tax_rate:tax_rates(name, rate, direction)
    )
  ),
  void_transaction:ledger_transactions!void_transaction_id(transaction_date)
`;
/**
 * AR invoice line items (`invoice_line_items` with `invoice_type = 'ar'`).
 * The 16 columns the detail read has always selected, `tax_rate_id`, the
 * service-category embed and the line's tax rate. `tax_rate_id` is read by the
 * channel-sale edit form (a sale re-posts from its lines, so the prefilled
 * line must name its rate); the document carries it as `taxRateId`.
 *
 * `tax_rate_ref` (20261101120000) is the line's own `tax_rates` row, aliased
 * because `tax_rate` is already the line's percentage column. The document
 * names its tax-rate groups from it: a DRAFT books nothing, so it has no
 * posting journal whose `tax_rate` embed could name them. The posting stays
 * the fallback for a legacy line with no `tax_rate_id`.
 * `source_expense_id` (migration 20260928141345) is the rebill link: the AR
 * edit form reads it so a re-save keeps each line's link to its expense. The
 * document never renders it.
 */
export const AR_INVOICE_LINE_ITEMS_SELECT = `
  id,
  invoice_id,
  service_category_id,
  revenue_account_id,
  description,
  quantity,
  unit_price,
  tax_rate,
  tax_rate_id,
  tax_treatment,
  tax_inclusive,
  exemption_reason,
  exemption_reason_other,
  tax_amount,
  line_total,
  line_total_with_tax,
  sort_order,
  source_expense_id,
  tax_rate_ref:tax_rates(name),
  service_category:service_categories(
    id,
    category_name,
    category_code,
    category_group
  )
`;
/**
 * AP bill detail — the `accounts_payable` row plus business/person, the
 * posting journal, the RAW `payment_transactions` array and the void journal.
 *
 * Deliberately NO project embeds (see this file's header). `select('*')` on
 * the header for the same reason as AR. `void_of_transaction_id` and
 * `payment_import_matches` are both load-bearing: the first keys
 * `filterPaymentTransactions`, the second is the only signal for the edit
 * flow's `hasImportLinkedPayment` guard.
 */
export const AP_INVOICE_DOCUMENT_SELECT = `
  *,
  business:businesses(name),
  person:people(first_name, last_name, email),
  ledger_transaction:ledger_transactions!ledger_transaction_id(
    transaction_number,
    fund_id,
    project_id,
    reference,
    ${IMPORT_MATCH_EMBED},
    entries:ledger_entries(
      *,
      chart_of_accounts(id, account_name, ${ACCOUNT_TYPE_EMBED}),
      tax_rate:tax_rates(name, rate, direction)
    )
  ),
  payment_transactions:ledger_transactions!ap_invoice_id(
    id,
    transaction_number,
    transaction_date,
    description,
    total_amount,
    reference,
    fund_id,
    void_of_transaction_id,
    ${IMPORT_MATCH_EMBED},
    payment_import_matches:accounting_import_matches!payment_transaction_id(id),
    entries:ledger_entries(
      *,
      chart_of_accounts(id, account_name, ${ACCOUNT_TYPE_EMBED}),
      tax_rate:tax_rates(name, rate, direction)
    )
  ),
  void_transaction:ledger_transactions!void_transaction_id(
    id,
    transaction_number,
    transaction_date,
    description,
    total_amount,
    entries:ledger_entries(
      *,
      chart_of_accounts(id, account_name, ${ACCOUNT_TYPE_EMBED}),
      tax_rate:tax_rates(name, rate, direction)
    )
  )
`;
/**
 * AP bill line items — the 16 `invoice_line_items` columns the AP detail read
 * pinned (no service-category embed: AP lines carry `expense_account_id`, and
 * the AP reads never selected one). Pinned so the wire type
 * (`APBillLineItemWire`) and the payload cannot drift.
 */
export const AP_INVOICE_LINE_ITEM_COLUMNS = "id, invoice_id, expense_account_id, description, quantity, unit_price, tax_rate, tax_rate_id, tax_treatment, tax_inclusive, exemption_reason, exemption_reason_other, tax_amount, line_total, line_total_with_tax, sort_order";
//# sourceMappingURL=selects.js.map